import React, { useEffect, useState, useCallback } from "react";
import { UserPlus } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import ConfirmDialog from "@/components/ConfirmDialog";
import { fmtDate } from "@/components/PageHeader";

// Pending signup applications, shown at the top of the Roster (hidden when there are none).
export default function PendingApplications({ onChanged }) {
  const [pending, setPending] = useState([]);
  const [confirm, setConfirm] = useState(null); // {member, action}
  const { viewOnly } = useAuth();

  const load = useCallback(async () => {
    const { data } = await api.get("/coach/assignment-requests");
    setPending((data.items || []).filter((i) => i.assignment_status === "pending"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const doAction = async () => {
    if (!confirm) return;
    try {
      await api.post(`/coach/assignment-requests/${confirm.member.id}/${confirm.action}`);
      toast.success(confirm.action === "approve" ? "Member approved" : "Member rejected");
      setConfirm(null); load(); onChanged?.();
      window.dispatchEvent(new Event("rpg:badges-refresh"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (pending.length === 0) return null;
  return (
    <Card className="mb-6 overflow-hidden border-amber-200 shadow-sm" data-testid="pending-applications-card">
      <div className="flex items-center gap-2 border-b border-amber-100 bg-amber-50/60 px-4 py-3">
        <UserPlus className="h-4 w-4 text-amber-700" />
        <h3 className="text-sm font-bold uppercase tracking-wide text-amber-800">Pending Applications</h3>
        <span className="rounded-full bg-amber-200 px-2 py-0.5 text-xs font-bold text-amber-800 tabular-nums" data-testid="pending-applications-count">{pending.length}</span>
      </div>
      <div className="overflow-x-auto">
        <Table data-testid="requests-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              {["Name", "Email", "Requested", "Status", "Actions"].map((h) => (
                <TableHead key={h} className={`text-xs font-bold uppercase tracking-wide text-slate-500 ${h === "Name" ? "sticky left-0 z-10 bg-slate-50" : ""}`}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pending.map((m) => (
              <TableRow key={m.id} data-testid={`request-row-${m.id}`}>
                <TableCell className="sticky left-0 z-10 whitespace-nowrap bg-white font-semibold text-slate-800">{m.first_name} {m.last_name}</TableCell>
                <TableCell className="text-sm text-slate-500">{m.email}</TableCell>
                <TableCell className="text-sm">{fmtDate(m.created_at)}</TableCell>
                <TableCell><StatusBadge status={m.assignment_status} /></TableCell>
                <TableCell>
                  {!viewOnly && (
                    <div className="flex gap-2">
                      <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => setConfirm({ member: m, action: "approve" })} data-testid={`approve-request-${m.id}`}>Approve</Button>
                      <Button size="sm" variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50" onClick={() => setConfirm({ member: m, action: "reject" })} data-testid={`reject-request-${m.id}`}>Reject</Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <ConfirmDialog
        open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.action === "approve" ? "Approve member?" : "Reject member?"}
        description={confirm ? `${confirm.member.first_name} ${confirm.member.last_name} will be ${confirm.action === "approve" ? "able to log in." : "rejected."}` : ""}
        confirmText={confirm?.action === "approve" ? "Approve" : "Reject"}
        destructive={confirm?.action === "reject"} onConfirm={doAction} testId="request-confirm-dialog"
      />
    </Card>
  );
}
